/**
 * The card processor a company connects, and what it needs to be told before
 * it will move money.
 *
 * ONE ENTRY, and Settings asks for its credentials rather than offering a
 * choice — a select with one option in it is a question whose answer the
 * reader has to look up to discover there was never more than one. The table
 * stays a table because three separate things have to agree about a
 * processor's shape and can never be allowed to drift: the Settings form that
 * collects the credentials, the Zod schema that validates them, and the
 * adapter that spends them. A second processor would be a new entry here plus
 * a new adapter module — nothing else in the app enumerates them.
 *
 * The split between `secretField` and `extraFields` mirrors how the row is
 * stored (migration 0037): the primary secret keeps its own encrypted column,
 * because `secret_key_last4` and "is this connected?" are derived from it,
 * and everything else travels as one encrypted JSON object beside it.
 *
 * Subscriptions are NOT on this list. The app's own billing runs on the
 * deployment's Stripe account and is not something a tenant configures: what
 * a company's customers pay through is separate from what the company pays
 * us through.
 */

export const PAYMENT_PROVIDERS = ['stripe'] as const;
export type PaymentProviderId = (typeof PAYMENT_PROVIDERS)[number];

export function isPaymentProviderId(value: unknown): value is PaymentProviderId {
  return typeof value === 'string' && (PAYMENT_PROVIDERS as readonly string[]).includes(value);
}

/**
 * One credential the owner types in.
 *
 * `pattern` is a shape check, not a validity check — it catches a key pasted
 * into the wrong box, which is the mistake that actually happens, and leaves
 * "is this key real?" to the provider. Fields without a well-known shape carry
 * no pattern rather than a guessed one.
 */
export interface PaymentCredentialField {
  /** Stable API name. Also the i18n key suffix: `paymentSettings.fields.<name>`. */
  readonly name: string;
  /** English label, and the fallback when a locale has no string for it. */
  readonly label: string;
  /** Rendered as a password box and never echoed back by the API. */
  readonly secret: boolean;
  readonly hint: string;
  readonly pattern?: string;
  readonly patternMessage?: string;
  readonly maxLength: number;
  /**
   * Whether this value names WHICH merchant account settles the money, as
   * opposed to being one of the rotatable credentials used to reach it.
   *
   * Only these feed the account fingerprint, and the distinction has teeth.
   * Changing the account is meant to be a serious event: every card saved
   * against the old one is cleared, because a vault token means nothing to a
   * different merchant. Fingerprinting a rotatable value makes an ordinary key
   * rotation look like that — the owner rotates a browser-side key, every
   * customer's saved card is dropped, and every charge already taken becomes
   * unrefundable because its stamped identity no longer matches anything,
   * even though the same merchant account can still act on those
   * transactions.
   *
   * So: a merchant identifier — yes. An API key half, a browser token, a
   * bearer token — no. (Stripe has no such extra field: its whole identity
   * is its key, and its stable account id is looked up from the key.)
   */
  readonly identifiesAccount?: boolean;
}

export interface PaymentProviderDescriptor {
  readonly id: PaymentProviderId;
  readonly name: string;
  readonly description: string;
  /**
   * The credential stored in `secret_key_cipher`. Whichever value most
   * identifies the account AND must stay secret, so the last four shown in
   * Settings mean something to the person reading them.
   */
  readonly secretField: PaymentCredentialField;
  /** Stored together in `credentials_cipher`. */
  readonly extraFields: readonly PaymentCredentialField[];
  /**
   * The signing secret for inbound webhooks, when the provider signs them.
   * Absent means this provider settles synchronously and has no endpoint to
   * register — Settings hides the webhook block entirely rather than asking
   * for a secret that would never be checked.
   */
  readonly webhookField?: PaymentCredentialField;
  /**
   * Whether test-vs-live has to be chosen explicitly.
   *
   * Stripe's key says which it is (`sk_test_…` / `sk_live_…`), so asking would
   * only create a way to be wrong. Nothing else here encodes the environment
   * in a credential, so nothing else can be inferred.
   */
  readonly needsEnvironmentChoice: boolean;
  /** In-person card payments through a Terminal SDK. */
  readonly supportsTerminal: boolean;
  /**
   * Whether handing the office's own device to a customer is a sensible way
   * to take a payment on this processor — true where the hosted page is a
   * card form.
   */
  readonly supportsInPersonCardEntry: boolean;
  /**
   * Whether a card fee may be added to payments taken through this processor.
   *
   * A card fee is a surcharge for paying BY CARD, and adding it only holds
   * up where the checkout can guarantee that is what happened — which a
   * card-only Stripe Checkout can.
   */
  readonly supportsCardFee: boolean;
  /**
   * Where the customer enters their card. `provider_hosted` means the
   * processor serves the page itself (Stripe Checkout); `app_hosted` means
   * this app serves a page that loads the processor's browser-side tokenizer,
   * so the card still goes straight to the processor and never through this
   * Worker.
   */
  readonly checkout: 'provider_hosted' | 'app_hosted';
}

const STRIPE: PaymentProviderDescriptor = {
  id: 'stripe',
  name: 'Stripe',
  description: 'Cards through Stripe Checkout, with Tap to Pay on supported phones.',
  secretField: {
    name: 'secretKey',
    label: 'Secret key',
    secret: true,
    hint: 'From Stripe → Developers → API keys. A restricted key (rk_…) works too.',
    pattern: '^(sk|rk)_(test|live)_[A-Za-z0-9]{8,}$',
    patternMessage: 'That does not look like a Stripe secret key (sk_test_… or sk_live_…)',
    maxLength: 300,
  },
  extraFields: [],
  webhookField: {
    name: 'webhookSecret',
    label: 'Webhook signing secret',
    secret: true,
    hint: 'From the endpoint you added in Stripe → Developers → Webhooks.',
    pattern: '^whsec_[A-Za-z0-9+/=_-]{8,}$',
    patternMessage: 'That does not look like a Stripe webhook signing secret (whsec_…)',
    maxLength: 300,
  },
  needsEnvironmentChoice: false,
  supportsTerminal: true,
  supportsCardFee: true,
  supportsInPersonCardEntry: true,
  checkout: 'provider_hosted',
};

export const PAYMENT_PROVIDER_DESCRIPTORS: Readonly<Record<PaymentProviderId, PaymentProviderDescriptor>> = {
  stripe: STRIPE,
};

export function paymentProviderDescriptor(id: PaymentProviderId): PaymentProviderDescriptor {
  return PAYMENT_PROVIDER_DESCRIPTORS[id];
}

/**
 * What the office can do with a card IN PERSON on a connected account.
 *
 * The mobile app asks for this rather than switching on the provider name
 * itself: which processors have a Tap to Pay SDK is a fact about the
 * integration, and a phone that has to know the list would need shipping to
 * an app store every time it changed.
 *
 * `cardEntry` is the fallback that nearly always exists — the hosted card
 * page, opened on the office's own device with the customer present.
 * `tapToPay` is true only where a card can be read by the phone's own NFC,
 * which today is Stripe alone: it is the only one with a Terminal SDK this app
 * can drive from Flutter. The others take cards in person through their own
 * physical terminals, which is not something a phone can stand in for.
 */
export interface InPersonPaymentCapabilities {
  /** Read a card with the phone's NFC (Tap to Pay on iPhone / Android). */
  tapToPay: boolean;
  /** Open the processor's hosted card form on this device. */
  cardEntry: boolean;
}

export function inPersonCapabilities(id: PaymentProviderId): InPersonPaymentCapabilities {
  const descriptor = paymentProviderDescriptor(id);
  return { tapToPay: descriptor.supportsTerminal, cardEntry: descriptor.supportsInPersonCardEntry };
}

/** Every field a provider collects, primary secret first, webhook secret last. */
export function paymentProviderFields(id: PaymentProviderId): readonly PaymentCredentialField[] {
  const descriptor = paymentProviderDescriptor(id);
  return [
    descriptor.secretField,
    ...descriptor.extraFields,
    ...(descriptor.webhookField ? [descriptor.webhookField] : []),
  ];
}

/**
 * The extra fields that must be present for the account to be usable.
 *
 * Everything a provider lists is required except where its label says
 * otherwise — an incomplete set means requests that fail at the gateway with a
 * message the owner cannot act on, so the API refuses the save instead.
 */
export function requiredExtraFieldNames(id: PaymentProviderId): readonly string[] {
  return paymentProviderDescriptor(id)
    .extraFields.filter((field) => !field.label.includes('(optional)'))
    .map((field) => field.name);
}
